// The map wraps around (geometry in worldWrapMath.ts). Two parts:
//  * every frame, right after PIXI has drawn the map, the islands and
//    everything on them are drawn again at each copy offset that is on screen —
//    the same objects rendered with a shifted transform, so no extra sprites,
//    textures or memory;
//  * when the camera drifts more than half a period away it is moved back by a
//    whole period — the picture is identical, so the player never sees the jump.
//    Dragging and the fling keep working: pixi-viewport moves relatively.
// Copies are only pictures: tapping one does nothing until the camera has
// wrapped (half a period on), where the real objects are.
import { useEffect, type MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { MapSceneState } from './mapSceneState';
import { cameraWrapShift, ghostOffsets, wrapPeriod, type Rect, type WrapPeriod } from './worldWrapMath';

/** Houses, avatars and sale tags rise above their anchor point. */
const OBJECT_OVERHANG = 200;

let period: WrapPeriod | null = null;

/** The current wrap period (null until the islands are on the map). Read by culling. */
export function getWrapPeriod(): WrapPeriod | null {
  return period;
}

/** Everything drawn on the map: island sprites by their bounds, objects by their anchor. */
function contentBounds(islandLayer: PIXI.Container, objectsLayer: PIXI.Container): Rect | null {
  let r: Rect | null = null;
  const add = (minX: number, minY: number, maxX: number, maxY: number) => {
    r = r
      ? { minX: Math.min(r.minX, minX), minY: Math.min(r.minY, minY), maxX: Math.max(r.maxX, maxX), maxY: Math.max(r.maxY, maxY) }
      : { minX, minY, maxX, maxY };
  };
  for (const island of islandLayer.children) {
    const b = island.getLocalBounds();
    add(island.x + b.x, island.y + b.y, island.x + b.x + b.width, island.y + b.y + b.height);
  }
  for (const o of objectsLayer.children) {
    add(o.x - OBJECT_OVERHANG, o.y - OBJECT_OVERHANG, o.x + OBJECT_OVERHANG, o.y + OBJECT_OVERHANG);
  }
  return r;
}

export interface UseWorldWrapParams {
  sceneRef: MutableRefObject<MapSceneState>;
  isPixiAppReady: boolean;
  /** Markers rebuilt — the content may have changed. */
  markersReady: boolean;
  mapObjectsRenderKey: string;
}

export function useWorldWrap({ sceneRef, isPixiAppReady, markersReady, mapObjectsRenderKey }: UseWorldWrapParams): void {
  useEffect(() => {
    const { app, viewport } = sceneRef.current.pixi;
    const islandLayer = sceneRef.current.layers.island;
    const objectsLayer = sceneRef.current.layers.mapObjects;
    if (!isPixiAppReady || !markersReady || !app || !viewport || !islandLayer || !objectsLayer) return;

    // Islands and objects keep arriving after the first markers (they load in
    // parallel): measure again whenever either layer changes size.
    let measured = '';
    const measure = () => {
      const key = `${islandLayer.children.length}:${objectsLayer.children.length}`;
      if (key === measured) return;
      measured = key;
      const bounds = contentBounds(islandLayer, objectsLayer);
      period = bounds ? wrapPeriod(bounds) : null;
    };
    measure();

    const ghostTransform = new PIXI.Matrix();
    const drawCopies = () => {
      measure();
      if (!period || !viewport.visible || viewport.destroyed) return;
      const view = { minX: viewport.left, minY: viewport.top, maxX: viewport.right, maxY: viewport.bottom };
      for (const [dx, dy] of ghostOffsets(view, period)) {
        // The viewport's transform, moved by (dx, dy) in world units.
        ghostTransform.copyFrom(viewport.localTransform);
        ghostTransform.tx += ghostTransform.a * dx + ghostTransform.c * dy;
        ghostTransform.ty += ghostTransform.b * dx + ghostTransform.d * dy;
        app.renderer.render({ container: islandLayer, transform: ghostTransform, clear: false });
        app.renderer.render({ container: objectsLayer, transform: ghostTransform, clear: false });
      }
    };
    // After the map's own render (UPDATE_PRIORITY.LOW), in the same frame.
    app.ticker.add(drawCopies, null, PIXI.UPDATE_PRIORITY.UTILITY);

    const wrapCamera = () => {
      if (!period) return;
      const { dx, dy } = cameraWrapShift(viewport.center, period);
      if (dx === 0 && dy === 0) return;
      viewport.x -= dx * viewport.scale.x;
      viewport.y -= dy * viewport.scale.y;
      // Culling and everything tracking the camera re-read it.
      viewport.emit('moved', { viewport, type: 'wrap' } as never);
    };
    viewport.on('moved', wrapCamera);
    viewport.on('zoomed', wrapCamera);

    return () => {
      app.ticker.remove(drawCopies, null);
      viewport.off('moved', wrapCamera);
      viewport.off('zoomed', wrapCamera);
    };
  }, [sceneRef, isPixiAppReady, markersReady, mapObjectsRenderKey]);
}
